import React, { useState, useRef, useEffect } from 'react'
import Icon from '../common/Icon'

interface ChatInputProps {
  onSendMessage: (message: string) => void
  onCancelRequest: () => void
  isWaiting: boolean
  initialMessage?: string
  focusTrigger?: number
}

const ChatInput: React.FC<ChatInputProps> = ({
  onSendMessage,
  onCancelRequest,
  isWaiting,
  initialMessage = '',
  focusTrigger = 0,
}) => {
  const [message, setMessage] = useState(initialMessage)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  // Update message when initialMessage changes
  useEffect(() => {
    setMessage(initialMessage)
    // Focus the textarea when initialMessage changes
    if (textareaRef.current && initialMessage) {
      textareaRef.current.focus()
    }
  }, [initialMessage])

  // Focus textarea when focusTrigger changes
  useEffect(() => {
    if (focusTrigger > 0 && textareaRef.current) {
      textareaRef.current.focus()
    }
  }, [focusTrigger])

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto'
      textareaRef.current.style.height = `${Math.min(
        textareaRef.current.scrollHeight,
        200
      )}px`
    }
  }, [message])

  // Focus on input when component mounts and when isWaiting changes to false
  useEffect(() => {
    if (!isWaiting && textareaRef.current) {
      textareaRef.current.focus()
    }
  }, [isWaiting])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (message.trim() === '' || isWaiting) return

    onSendMessage(message)
    setMessage('')

    // Reset height
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto'
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSubmit(e)
    }
  }

  return (
    <div className="input-container">
      <form className="chat-input" onSubmit={handleSubmit}>
        <textarea
          ref={textareaRef}
          className="chat-input__textarea"
          value={message}
          onChange={e => setMessage(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Message BreadGPT..."
          disabled={isWaiting}
          rows={1}
          autoFocus
        />
        {isWaiting ? (
          <button
            type="button"
            className="chat-input__stop"
            onClick={onCancelRequest}
            aria-label="Stop request"
          />
        ) : (
          <button
            type="submit"
            className="chat-input__send"
            disabled={message.trim() === '' || isWaiting}
            aria-label="Send message"
          >
            <Icon name="send-arrow-up" size={22} />
          </button>
        )}
      </form>
    </div>
  )
}

export default ChatInput
